import React from 'react';
import { Link } from 'react-router-dom';
import Avatar from './Avatar';

/**
 * Avatar + name + `@identifier` cell for user/member lists (admin Miembros,
 * Usuarios, Superadmins). Inactive users render struck-through + greyscaled.
 *
 * `to` makes the name a router `<Link>` (e.g. `/usuarios/123`); omit it for
 * plain text — shared code must not hardcode app-specific routes.
 */
export interface UserCellUser {
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  email?: string | null;
  is_active?: boolean;
  avatar_url?: string | null;
}

export interface UserCellProps {
  user: UserCellUser;
  /** Optional link target for the name. Plain text when omitted. */
  to?: string;
}

export function userDisplayName(u: UserCellUser): string {
  const parts = [u.first_name, u.last_name].map((s) => (s || '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (u.username || u.email || '—');
}

const UserCell: React.FC<UserCellProps> = ({ user, to }) => {
  const name = userDisplayName(user);
  const identifier = user.username || user.email || name;
  const isActive = user.is_active !== false;
  const nameClass = `font-bold${
    !isActive ? ' line-through text-slate-400 dark:text-slate-500' : ' text-slate-800 dark:text-white'
  }`;
  return (
    <div className="flex items-center gap-3">
      <Avatar src={user.avatar_url} name={name} size={32} inactive={!isActive} />
      <div className="flex flex-col">
        {to ? (
          <Link to={to} className={nameClass}>{name}</Link>
        ) : (
          <span className={nameClass}>{name}</span>
        )}
        <span
          className={`text-[10px] tracking-tighter${
            !isActive ? ' line-through text-slate-400 dark:text-slate-500' : ' text-slate-500'
          }`}
        >
          @{identifier}
        </span>
      </div>
    </div>
  );
};

export default UserCell;
